import { Button, Spinner, Text } from '@/ui-kit'
import { Trans } from '@lingui/react/macro'
import { useQueryClient } from '@tanstack/react-query'
import { type ReactNode, useEffect, useRef, useState } from 'react'
import { useNectarProject, useNextTask } from '../../api'
import type { TaskStats } from '../../api/models'
import { useTaskHeartbeat } from '../../hooks/useTaskHeartbeat'
import { markProlificComplete } from '../../prolific/api'
import { clearProlificSession, getProlificCompletionUrl, isProlificMode } from '../../prolific/session'
import { NectarProjectStatusView, type TerminalStatus } from '../NectarProjectStatusView'
import { NectarTaskExpired } from '../NectarTaskExpired'
import { NectarTaskProgress } from '../NectarTaskProgress'
import { NectarTasksAllDone } from '../NectarTasksAllDone'
import { type ProjectFlowProps } from '../project-flows'
import { NoTasksAvailable } from './NoTasksAvailable'

type NextTaskTask = NonNullable<ReturnType<typeof useNextTask>['data']>['task']

type LazyTaskFlowProps = ProjectFlowProps & {
	renderBody: (args: { task: NextTaskTask; stats?: TaskStats }) => ReactNode
	/** Optional content rendered in the right slot of NectarTaskProgress (e.g. a skips badge). */
	progressRightSlot?: ReactNode
}

const isNoTasksError = (err: unknown): boolean =>
	(err as { response?: { status?: number } })?.response?.status === 409

export function LazyTaskFlow({
	projectId,
	projectName,
	rewardConfig,
	progress,
	maxTasksPerUser,
	onBack,
	onCompleted,
	renderBody,
	progressRightSlot,
}: LazyTaskFlowProps) {
	const queryClient = useQueryClient()
	const { data, isLoading, isError, error, refetch } = useNextTask(projectId)
	const { data: project } = useNectarProject(projectId)
	const enrollmentStatus = project?.enrollment?.status

	const noTasks = isError && isNoTasksError(error)
	const isAllDone = maxTasksPerUser > 0 && progress.completed >= maxTasksPerUser
	const atTerminal =
		isAllDone ||
		enrollmentStatus === 'PENDING_REVIEW' ||
		enrollmentStatus === 'EXPIRED' ||
		enrollmentStatus === 'APPROVED' ||
		enrollmentStatus === 'REJECTED'

	const [released, setReleased] = useState(false)

	// Stop the heartbeat at a terminal so we don't beat against a finished assignment. A 404/409
	// (assignment expired) flips `released` so the user sees a "time's up" card, not the dead task.
	useTaskHeartbeat({
		projectId,
		taskId: data?.task.id,
		enabled: !atTerminal && !released,
		onClosed: () => setReleased(true)
	})

	// Once a fresh task arrives (new id) the card clears and the body renders again.
	useEffect(() => {
		setReleased(false)
	}, [data?.task.id])

	// Empty /next finalizes the enrollment server-side (audio). Pull the fresh status so we
	// render the right terminal instead of a stale "in progress".
	useEffect(() => {
		if (noTasks) {
			queryClient.invalidateQueries({ queryKey: ['NECTAR_PROJECT', projectId] })
		}
	}, [noTasks, projectId, queryClient])

	// Fire onCompleted when the user reaches the all-done state (non-prolific path).
	const completedFiredRef = useRef(false)
	useEffect(() => {
		if (isAllDone && !completedFiredRef.current) {
			completedFiredRef.current = true
			onCompleted()
		}
	}, [isAllDone, onCompleted])

	// Prolific worker finishing the study: mark complete + redirect (once).
	const prolificFiredRef = useRef(false)
	useEffect(() => {
		const done = atTerminal && (enrollmentStatus === 'PENDING_REVIEW' || isAllDone)
		if (done && isProlificMode() && !prolificFiredRef.current) {
			prolificFiredRef.current = true
			const url = getProlificCompletionUrl()
			markProlificComplete()
				.catch(() => {})
				.finally(() => {
					clearProlificSession()
					if (url) window.location.href = url
				})
		}
	}, [atTerminal, enrollmentStatus, isAllDone])

	if (isAllDone || enrollmentStatus === 'PENDING_REVIEW') {
		return (
			<NectarTasksAllDone
				name={projectName}
				rewardConfig={{ type: rewardConfig?.type ?? 'points', amount: rewardConfig?.amount ?? 0 }}
				projectId={projectId}
			/>
		)
	}
	if (enrollmentStatus === 'EXPIRED' && project) return <NectarTaskExpired project={project} />
	if ((enrollmentStatus === 'APPROVED' || enrollmentStatus === 'REJECTED') && project) {
		return <NectarProjectStatusView project={project} status={enrollmentStatus as TerminalStatus} />
	}

	if (isLoading) {
		return (
			<div className="flex w-full justify-center py-10">
				<Spinner />
			</div>
		)
	}

	if (noTasks) return <NoTasksAvailable onBack={onBack} />
	if (isError) return <NoTasksAvailable onBack={onBack} onRetry={() => void refetch()} retry />
	if (!data) return null

	if (released) {
		return (
			<div className="mx-auto mt-10 flex w-full max-w-[600px] flex-col items-center gap-4 rounded-2xl border border-solid border-gray-100 px-10 py-12 text-center">
				<Text type="h5" className="text-gray-70">
					<Trans>Time&rsquo;s up for this task</Trans>
				</Text>
				<Text type="body2-regular" className="text-gray-80">
					<Trans>It was released after a while of inactivity. Grab a fresh one to keep going.</Trans>
				</Text>
				<Button
					size="small"
					className="mt-2"
					onClick={() => void queryClient.invalidateQueries({ queryKey: ['NECTAR_NEXT_TASK', projectId] })}
				>
					<Trans>Get a new task</Trans>
				</Button>
			</div>
		)
	}

	return (
		<div className="flex flex-col gap-4">
			<NectarTaskProgress completed={progress.completed} total={maxTasksPerUser} rightSlot={progressRightSlot} />
			{/* key by task id so the body (and the recorder's internal state) remounts on each new task */}
			<div key={data.task.id} className="mx-auto w-full max-w-[600px]">
				{renderBody({ task: data.task, stats: data.stats })}
			</div>
		</div>
	)
}
